import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "../../../../singleton/axios";

export interface NewsItem {
  heading: string;
  description: string;
}

interface StructuredDevelopments {
  items: NewsItem[];
  generatedAt: string;
}

export interface DateRange {
  from: string; // ISO date (YYYY-MM-DD)
  to: string; // ISO date (YYYY-MM-DD)
}

export interface RecentDevelopmentsResponse {
  success: boolean;
  symbol: string;
  status?: "ready" | "generating";
  message?: string;
  developments?: StructuredDevelopments;
  citations?: string[];
  marketCapTier?: string;
  dateRange?: DateRange;
  cachedAt?: string;
  expiresAt?: string;
  error?: string;
  reason?: string;
}

export const useRecentDevelopments = (ticker: string) => {
  const query = useQuery<RecentDevelopmentsResponse>({
    queryKey: ["recent-developments", ticker],
    queryFn: async () => {
      const { data, status } = await api.post<RecentDevelopmentsResponse>(
        `/recent-developments/generate`,
        { symbol: ticker },
        { validateStatus: (s) => s < 500 }
      );
      if (status === 202) {
        return { ...data, status: "generating" as const };
      }
      return data;
    },
    enabled: !!ticker,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Poll when generation is in progress
  useEffect(() => {
    if (query.data?.status === "generating") {
      const timeout = setTimeout(() => {
        query.refetch();
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [query.data?.status, query.refetch]);

  return query;
};

export type RecentDevelopmentsStatus = "loading" | "generating" | "ready" | "error" | "no-data";

export const getRecentDevelopmentsStatus = (
  data: RecentDevelopmentsResponse | undefined,
  isLoading: boolean,
  error: unknown
): RecentDevelopmentsStatus => {
  if (error) return "error";
  if (isLoading && !data) return "loading";
  if (data?.status === "generating") return "generating";
  if (data?.developments?.items?.length) return "ready";
  if (data?.reason === "fund_or_etf") return "no-data";
  return "no-data";
};
